import { Component, ElementRef, HostListener, OnDestroy, OnInit, signal, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { Subject, takeUntil, interval } from 'rxjs';

import { AuthService } from '../shared/services/auth.service';
import { environment } from '../../environments/environment';
import { AGENT_BUILDER_SERVICE } from '../shared/services/agent-builder.token';
import { ThemeService } from '../shared/services/theme.service';
import { ShellContextService } from '../shared/services/shell-context.service';
import { UsageService } from '../shared/services/usage.service';
import { DevPanelComponent } from '../shared/components/dev-panel/dev-panel.component';
import { IconComponent } from '../shared/components/icon/icon.component';
import { StatusPillComponent } from '../shared/components/status-pill/status-pill.component';
import { ToastComponent } from '../shared/components/toast/toast.component';
import { ConfirmDialogComponent } from '../shared/components/confirm-dialog/confirm-dialog.component';
import { SendFeedbackDialogComponent } from '../shared/components/send-feedback-dialog/send-feedback-dialog.component';
import { ConversationTurn } from '../shared/services/feedback.service';
import { ReadinessPillComponent } from './readiness-pill.component';
import { UsageMeterComponent } from './usage-meter.component';

@Component({
  selector: 'app-studio-shell',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
    DevPanelComponent,
    IconComponent,
    StatusPillComponent,
    ToastComponent,
    ConfirmDialogComponent,
    SendFeedbackDialogComponent,
    ReadinessPillComponent,
    UsageMeterComponent,
  ],
  templateUrl: './studio-shell.component.html',
  styleUrls: ['./studio-shell.component.scss'],
})
export class StudioShellComponent implements OnInit, OnDestroy {
  userName = signal<string>('');
  currentRole = signal<string>('');
  pendingApprovalsCount = signal<number>(0);

  /** Avatar dropdown that hosts the "my usage" widget. */
  readonly usageMenuOpen = signal<boolean>(false);

  /** Send Feedback modal (opened from the avatar menu). */
  readonly feedbackOpen = signal<boolean>(false);
  /** Co-builder transcript snapshotted when the dialog opens — a stable copy,
   *  so the dialog's opt-in preview doesn't shift under the user while the
   *  co-builder keeps streaming. */
  readonly feedbackConversation = signal<ConversationTurn[]>([]);

  readonly mockMode = signal<boolean>(environment.useMockData);
  readonly helpUrl = environment.HELP_PORTAL_URL;
  readonly isAdmin = computed(() => ['admin', 'super_admin'].includes(this.currentRole().toLowerCase()));
  /** Who may review the approvals queue: admins plus the dedicated approver role.
   *  Gates only the approvals nav link + count fetch; publishing stays admin-only. */
  readonly canApprove = computed(() => ['admin', 'super_admin', 'approver'].includes(this.currentRole().toLowerCase()));
  /** A plain approver may ONLY use the approvals queue — hide every other Studio
   *  tab (Create / My Agents / Abilities). admin/super_admin who can also approve
   *  keep full access; this is the dedicated approve-only role. */
  readonly isApproverOnly = computed(() => this.currentRole().toLowerCase() === 'approver');
  readonly initials = computed(() => {
    const name = this.userName();
    if (!name) return '?';
    const parts = name.split('@')[0].split(/[._-]/).filter(Boolean);
    return parts.slice(0, 2).map((p) => p[0]!.toUpperCase()).join('') || name[0]!.toUpperCase();
  });

  private readonly destroying$ = new Subject<void>();
  private readonly auth = inject(AuthService);
  private readonly agentBuilder = inject(AGENT_BUILDER_SERVICE);
  readonly theme = inject(ThemeService);
  readonly shellContext = inject(ShellContextService);
  readonly usage = inject(UsageService);
  private readonly host = inject(ElementRef<HTMLElement>);

  ngOnInit(): void {
    this.userName.set(this.auth.currentUserName$.value ?? '');
    this.currentRole.set(sessionStorage.getItem('current-role') ?? '');
    this.auth.currentUserName$
      .pipe(takeUntil(this.destroying$))
      .subscribe((u) => this.userName.set(u ?? ''));

    // Builder's own token usage for the avatar menu. Fails soft — never blocks
    // the shell. NOTE: there's no obvious post-turn hook in the shell itself;
    // re-fetched on shell init and whenever the user opens the avatar menu so
    // the numbers are fresh on view. A co-builder/test-turn callback could also
    // call usage.refresh() if one is wired later.
    this.usage.refresh();

    // Poll approvals count every 30s when admin (only used by mock; cheap call).
    this.refreshApprovalsCount();
    interval(30_000)
      .pipe(takeUntil(this.destroying$))
      .subscribe(() => this.refreshApprovalsCount());

    // React to role changes in the same tab (dev-panel) via storage event in other tabs.
    window.addEventListener('storage', this.onStorageChange);
  }

  onLogout(): void {
    this.usage.reset();
    this.auth.logout();
  }

  /** Open the help portal in a new tab, passing the current session via the
   *  `#session=<base64>` hash fragment so the help portal can hydrate its own
   *  soft session gate without hitting a backend. Same encoding used by
   *  wg-agents-ui `HubSurfacesService.openPortal()`. */
  openHelpPortal(): void {
    const payload = {
      'id-token': sessionStorage.getItem('id-token') ?? '',
      'current-role': sessionStorage.getItem('current-role') ?? '',
      'current-username': sessionStorage.getItem('current-username') ?? '',
      'current-email': sessionStorage.getItem('current-email') ?? '',
      STATIC: sessionStorage.getItem('STATIC') ?? '',
    };
    const encoded = btoa(String.fromCodePoint(...new TextEncoder().encode(JSON.stringify(payload))));
    window.open(`${this.helpUrl}/#session=${encoded}`, '_blank', 'noopener');
  }

  // ── Send Feedback ──────────────────────────────────────────────

  openFeedback(): void {
    this.usageMenuOpen.set(false);
    this.feedbackConversation.set(this.shellContext.builder()?.feedbackConversation?.() ?? []);
    this.feedbackOpen.set(true);
  }

  closeFeedback(): void {
    this.feedbackOpen.set(false);
  }

  /** Toggle the avatar usage menu; refresh on open so the numbers are current. */
  toggleUsageMenu(): void {
    const next = !this.usageMenuOpen();
    this.usageMenuOpen.set(next);
    if (next) this.usage.refresh();
  }

  /** Close the usage menu on any click outside the shell host's open menu. */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.usageMenuOpen()) return;
    const target = event.target as Node;
    const menu = this.host.nativeElement.querySelector('.user-menu');
    if (menu && !menu.contains(target)) {
      this.usageMenuOpen.set(false);
    }
  }

  ngOnDestroy(): void {
    this.destroying$.next();
    this.destroying$.complete();
    window.removeEventListener('storage', this.onStorageChange);
  }

  private readonly onStorageChange = (e: StorageEvent): void => {
    if (e.key === 'current-role') {
      this.currentRole.set(e.newValue ?? '');
    }
  };

  private refreshApprovalsCount(): void {
    if (!this.canApprove()) {
      this.pendingApprovalsCount.set(0);
      return;
    }
    this.agentBuilder.listApprovals().subscribe({
      next: (list) => this.pendingApprovalsCount.set(list.length),
      error: () => this.pendingApprovalsCount.set(0),
    });
  }
}
